import type { RouteLocationNormalized } from 'vue-router';
import { resolveReportShopUid } from '~/composables/useReportShopUid';
import { useAuthStore } from '~/store/auth';

/**
 * Query keys that select the tab of a page. A change in one of them is a new page view. A change in
 * any other query key is a filter change, and report pages write every filter to the query.
 */
const TAB_QUERY_KEYS = ['tab', 'table', 'view', 'chart'];

const getTab = (route: RouteLocationNormalized): string => {
  const value = TAB_QUERY_KEYS.map(key => route.query[key]).find(v => typeof v === 'string');
  return typeof value === 'string' ? value : '';
};

/**
 * A list page opens a create or edit form in a sheet with the `item` query key (see
 * `useEditSheet()`). The sheet counts as the page of the form, with the same path pattern, so the
 * record key never reaches GA4.
 */
const getSheetSuffix = (route: RouteLocationNormalized): string => {
  const item = route.query.item;
  if (typeof item !== 'string' || item === '') return '';
  return item === 'new' ? '/new' : '/:item';
};

/**
 * Custom GA4 page_view tracking.
 *
 * gtag.js's automatic page_view is disabled (see nuxt.config.ts
 * `gtag.config.send_page_view: false`). We send page_view manually here using
 * the matched route pattern (e.g. `/menu-items/:uid`) instead of the actual
 * path so dynamic route params never reach GA4 as distinct page paths.
 *
 * The page context goes into a repeated `gtag('config')` call, not only into page_view. GA4 then
 * attaches it to every later event, which includes `user_engagement` (time on page) and the report
 * events. Without it, those events carry the real URL with record IDs and no shop or organization.
 * `gtag('set')` is not an alternative: GA4 drops custom params given to it.
 */
export default defineNuxtPlugin(() => {
  const router = useRouter();
  const { gtag } = useGtag();
  const authStore = useAuthStore();
  const { id: gtagId, config: gtagConfig } = useRuntimeConfig().public.gtag;

  router.afterEach((to, from, failure) => {
    if (failure || !gtagId) return;

    // A page sets `analyticsRealPath` in its page meta when its path holds no record IDs and the
    // pattern alone does not tell which page the user reads.
    const basePath = to.meta.analyticsRealPath === true ? to.path : getMatchedRoutePath(to);
    const pagePath = `${basePath}${getSheetSuffix(to)}`;
    const tab = getTab(to);
    const shopUid = pagePath.startsWith('/report-')
      ? resolveReportShopUid(to.query.shop_uid, authStore)
      : (authStore.currentShop?.uid ?? '');

    // The base config keeps `send_page_view: false`. Without it, each config call sends a page_view.
    // Without `update: true`, the Google tag ignores a second config call for the same ID as a
    // duplicate, unless the stream turns off "Ignore duplicate instances on page". `update` merges
    // these values into the existing config.
    gtag('config', gtagId, {
      ...gtagConfig,
      update: true,
      page_location: `${window.location.origin}${pagePath}`,
      page_path: pagePath,
      report_tab: tab,
      shop_uid: shopUid,
      organization_id: authStore.currentOrganization?.id ?? 0,
      // The internal operator ID lets GA4 count people, not browsers. Never send the email or name.
      user_id: authStore.operator ? String(authStore.operator.id) : undefined,
    });

    // The first navigation comes from START_LOCATION, which matches no route.
    const isSameView =
      from.matched.length > 0 &&
      from.path === to.path &&
      getTab(from) === tab &&
      getSheetSuffix(from) === getSheetSuffix(to) &&
      from.query.item === to.query.item;
    if (!isSameView) {
      gtag('event', 'page_view', { page_path: pagePath });
    }
  });
});
